import React from 'react';
import {
  FunctionField,
  Datagrid,
  usePermissions,
  Filter,
  TextInput,
  ListController,
  ListContextProvider,
  Button,
  useTranslate,
} from 'react-admin';
import VeenguIcon from '~/components/VeenguIcon';
import { Typography } from '@material-ui/core';
import { withStyles, createStyles, makeStyles } from '@material-ui/core/styles';
import { ListView } from '~/layout';
import { DateTimeInput } from '~/components/ra';
import { SelectInput } from 'react-admin';
import { TextField } from '~/components';
import palette from '~/theme/palette';
import { history } from '~/App';
import { Draft } from '~/types/Draft';
import { time } from '~/utils';
import { ALL_TABS, draftPermissions, getRecordPage } from '~/utils/recordPages';
import { draftIdPostfix } from '~/hooks/draft';

export type DraftResource = 'individuals' | 'businesses' | 'pricings';

/** Only what the list adds on top of the record page registry. */
const draftResourceIcon: Record<DraftResource, React.ReactElement> = {
  individuals: <VeenguIcon name='user' />,
  businesses: <VeenguIcon name='store' />,
  pricings: <VeenguIcon name='tags' />,
};

const draftShowPath = (draftResource: DraftResource): string => {
  const path = getRecordPage(draftResource)?.path;
  return !path || path === ALL_TABS ? '/show' : path;
};

export const getDraftResource = (resource?: string): DraftResource | undefined => {
  const [draftResource] = resource?.split('/') ?? [];
  if (!draftResource || !getRecordPage(draftResource)?.drafts) return undefined;
  return draftResource as DraftResource;
};

export const l = (key: string): string => `resources.drafts.${key}`;
const label = (key: string): string => l(`fields.${key}`);

const listBodyStyles = (theme: any) => {
  return createStyles({
    content: {
      fontSize: 14,
      paddingLeft: 16,
      paddingRight: 16,
      [theme.breakpoints.up('sm')]: {
        paddingLeft: 24,
        paddingRight: 24,
      },
      paddingTop: 16,
      paddingBottom: 16,
    },
  });
};

const DraftsList = (props: any) => {
  const { permissions } = usePermissions();
  const classes = useStyles();
  const translate = useTranslate();

  const draftResource = getDraftResource(props.resource);

  if (!permissions || !draftResource) return null;

  const icon = draftResourceIcon[draftResource];

  return (
    <ListController {...props}>
      {(controllerProps: any) => {
        return (
          <ListContextProvider value={controllerProps}>
            <ListView
              title={
                <div className={classes.headline}>
                  <Typography variant='h2'>{translate(l(`titles.${draftResource}`))}</Typography>
                </div>
              }
              filters={<DraftsFilters draftResource={draftResource} />}
              {...controllerProps}
              pagination={controllerProps.loading || controllerProps.total === 0 ? null : undefined}
              bulkActionButtons={false}
              actions={
                <Button
                  label={translate(`resources.${draftResource}.name`, 2)}
                  onClick={() => history.push(`/${draftResource}`)}
                >
                  {icon}
                </Button>
              }
            >
              <ListBody draftResource={draftResource} />
            </ListView>
          </ListContextProvider>
        );
      }}
    </ListController>
  );
};

const ListBody = withStyles(listBodyStyles)((props: any) => {
  const translate = useTranslate();
  const { total, loading, classes } = props;
  const draftResource: DraftResource = props.draftResource;
  const t = (key: string): string => translate(label(key));

  if (loading) {
    return <div className={classes.content}>{t('searchInProgress')}</div>;
  } else if (total === 0) {
    return <div className={classes.content}>{t('noResultsFound')}</div>;
  }
  return (
    <>
      <Datagrid
        {...props}
        rowClick={(_id: any, _basePath: any, record: Draft) =>
          history.push(
            `/${draftResource}/${record?.id}${draftIdPostfix}${draftShowPath(draftResource)}`
          )
        }
        size='small'
        selectedIds={null}
      >
        <FunctionField
          label={t('title')}
          sortable={false}
          render={(record: any) => {
            return record?.title;
          }}
        />
        <FunctionField
          label={t('createdAt')}
          sortable={false}
          render={(record: Draft) => {
            const createdAt = time(record.createdAt);
            return <TextField label={createdAt.format('LL')}>{createdAt.format('LTS')}</TextField>;
          }}
        />
        <FunctionField
          label={t('updatedAt')}
          sortable={false}
          render={(record: Draft) => {
            const updatedAt = time(record.updatedAt);
            return <TextField label={updatedAt.format('LL')}>{updatedAt.format('LTS')}</TextField>;
          }}
        />
        <FunctionField
          label={t('type')}
          sortable={false}
          render={(record: Draft) => {
            return record.isUpdateDraft ? (
              <TextField style={{ color: palette.warning.main }}>{t('updateType')}</TextField>
            ) : (
              <TextField style={{ color: palette.success.main }}>{t('createType')}</TextField>
            );
          }}
        />
      </Datagrid>
    </>
  );
});

const DraftsFilters = (props: any) => {
  const classes = useStyles();
  const { permissions } = usePermissions();

  const { draftResource } = props;
  const page = getRecordPage(draftResource);
  const { update, create } = draftPermissions(page);
  const hasPermissionEdit = permissions?.includes(update);
  const hasPermissionCreate = permissions?.includes(create);

  const typeChoices = [
    { id: '', name: label('allDrafts') },
    ...(hasPermissionCreate ? [{ id: 'false', name: label('onlyCreate') }] : []),
    ...(hasPermissionEdit ? [{ id: 'true', name: label('onlyUpdate') }] : []),
  ];

  return (
    <Filter {...props} className={classes.form}>
      <DateTimeInput
        className={classes.formControl}
        label={label('createdAtFrom')}
        source='createdFrom'
        options={{ format: 'LL LT', clearable: true }}
        alwaysOn
      />
      <DateTimeInput
        className={classes.formControl}
        label={label('createdAtTo')}
        source='createdTo'
        options={{ format: 'LL LT', clearable: true }}
        alwaysOn
      />
      <SelectInput
        className={classes.formControl}
        label={label('type')}
        source='isUpdateDraft'
        allowEmpty={false}
        choices={typeChoices}
        alwaysOn
      />
      <TextInput
        className={classes.formControl}
        label={label('objectId')}
        source='objectId'
        alwaysOn
      />
    </Filter>
  );
};

const useStyles = makeStyles((theme) => ({
  form: {
    '& > div': {
      '& > div:last-child': {
        width: theme.spacing(1),
      },
    },
  },
  formControl: {
    width: 200,
  },
  headline: {
    marginTop: '8px',
    display: 'flex',
  },
  multiselect: {
    width: 300,
    '& > .MuiInputBase-root ': {
      minHeight: 46,
      '& > .MuiSelect-root': {
        paddingTop: 12,
        paddingBottom: 0,
        marginBottom: -4,
      },
    },
  },
}));

export default DraftsList;
